import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useThemeMode } from '../../contexts/ThemeContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { supabase } from '../../lib/supabase'
import { OAUTH_PROVIDERS, type UserIdentity } from '../../types/database'
import {
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Skeleton,
  Banner,
  Modal,
  useToast
} from 'wss3-forge'
import {
  Dismiss20Regular,
  Add20Regular,
  CheckmarkCircle20Filled,
  Link20Regular
} from '@fluentui/react-icons'

const PLACEHOLDER_EMAIL_DOMAIN = '@cfx.webba-creative.com'

export function LinkedAccounts() {
  const { user } = useAuth()
  const { mode } = useThemeMode()
  const toast = useToast()
  usePageTitle('Linked Accounts')

  const [identities, setIdentities] = useState<UserIdentity[]>([])
  const [loading, setLoading] = useState(true)
  const [unlinking, setUnlinking] = useState<string | null>(null)
  const [showUnlinkModal, setShowUnlinkModal] = useState(false)
  const [selectedIdentity, setSelectedIdentity] = useState<UserIdentity | null>(null)

  // Handle callback after linking a provider
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const linkedProvider = params.get('linked')

    if (linkedProvider) {
      // Remove the query param from URL
      window.history.replaceState({}, '', window.location.pathname)
      // Show success toast
      toast.success(`${linkedProvider.charAt(0).toUpperCase() + linkedProvider.slice(1)} account linked successfully`)
    }
  }, [])

  useEffect(() => {
    if (user) {
      loadIdentities()
    }
  }, [user?.id])

  const loadIdentities = async () => {
    if (!user) return

    setLoading(true)
    try {
      // First, sync identities from Supabase auth to user_identities table
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      const authIdentities = currentUser?.identities || []

      // Sync each auth identity to user_identities table
      for (const identity of authIdentities) {
        if (identity.provider !== 'email') {
          await supabase.from('user_identities').upsert({
            user_id: user.id,
            provider: identity.provider,
            provider_user_id: identity.id,
            provider_username: identity.identity_data?.name || identity.identity_data?.user_name || identity.identity_data?.preferred_username || null,
            provider_email: identity.identity_data?.email || null,
            provider_avatar_url: identity.identity_data?.avatar_url || identity.identity_data?.picture || null
          }, {
            onConflict: 'provider,provider_user_id'
          })
        }
      }

      // Then load from user_identities table
      const { data, error } = await supabase.rpc('get_user_identities', {
        p_user_id: user.id
      })

      if (error) throw error

      // Filter out 'email' provider - it's not an OAuth identity
      const oauthIdentities = (data || []).filter((i: any) => i.provider !== 'email')
      setIdentities(oauthIdentities)
    } catch (err) {
      console.error('Failed to load identities:', err)
      toast.error('Failed to load linked accounts')
    } finally {
      setLoading(false)
    }
  }

  const handleUnlinkClick = (identity: UserIdentity) => {
    setSelectedIdentity(identity)
    setShowUnlinkModal(true)
  }

  const handleUnlink = async () => {
    if (!selectedIdentity || !user) return

    // Check if this is the last identity and user has placeholder email
    const hasPlaceholderEmail = user.email?.includes(PLACEHOLDER_EMAIL_DOMAIN)
    if (identities.length <= 1 && hasPlaceholderEmail) {
      toast.error('Cannot unlink the last account. Please add an email first.')
      setShowUnlinkModal(false)
      return
    }

    setUnlinking(selectedIdentity.id)
    try {
      // First, unlink from Supabase Auth to prevent re-sync
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      const authIdentity = currentUser?.identities?.find(
        i => i.provider === selectedIdentity.provider &&
             i.id === selectedIdentity.provider_user_id
      )

      if (authIdentity) {
        const { error: unlinkError } = await supabase.auth.unlinkIdentity(authIdentity)
        if (unlinkError) {
          // If identity not found in auth, it may already be unlinked - continue to clean up our table
          if (!unlinkError.message?.toLowerCase().includes('not found')) {
            throw unlinkError
          }
        }
      }

      // Then clean up our user_identities table
      const { error } = await supabase.rpc('unlink_user_identity', {
        p_user_id: user.id,
        p_identity_id: selectedIdentity.id
      })

      if (error) throw error

      toast.success(`${getProviderLabel(selectedIdentity.provider)} account unlinked`)
      setIdentities(prev => prev.filter(i => i.id !== selectedIdentity.id))
    } catch (err: any) {
      console.error('Failed to unlink identity:', err)
      toast.error(err.message || 'Failed to unlink account')
    } finally {
      setUnlinking(null)
      setShowUnlinkModal(false)
      setSelectedIdentity(null)
    }
  }

  const handleLinkProvider = async (provider: string) => {
    if (provider === 'cfxre') {
      // Cfx.re needs special handling - redirect to Edge Function with link_to_user
      try {
        const { data, error } = await supabase.functions.invoke('cfx-auth-init', {
          body: {
            redirect_uri: null,
            app_id: null,
            link_to_user: user?.id
          }
        })

        if (error) throw error
        if (data?.redirect_url) {
          window.location.href = data.redirect_url
        }
      } catch (err: any) {
        console.error('Failed to initiate Cfx.re linking:', err)
        toast.error('Failed to connect with Cfx.re')
      }
    } else {
      // Standard OAuth providers - use Supabase linkIdentity to link to existing account
      try {
        const { error } = await supabase.auth.linkIdentity({
          provider: provider as 'google' | 'azure' | 'github' | 'discord',
          options: {
            redirectTo: `${window.location.origin}/linked-accounts?linked=${provider}`
          }
        })

        if (error) {
          throw error
        }
      } catch (err: any) {
        console.error('Failed to link provider:', err)
        toast.error(err.message || 'Failed to link account')
      }
    }
  }

  const getProviderLabel = (provider: string): string => {
    const info = OAUTH_PROVIDERS.find(p => p.id === provider)
    return info?.label || provider
  }

  const getProviderIcon = (provider: string) => {
    const icons: Record<string, JSX.Element> = {
      google: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      ),
      azure: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M0 0h11.5v11.5H0z" fill="#F25022" />
          <path d="M12.5 0H24v11.5H12.5z" fill="#7FBA00" />
          <path d="M0 12.5h11.5V24H0z" fill="#00A4EF" />
          <path d="M12.5 12.5H24V24H12.5z" fill="#FFB900" />
        </svg>
      ),
      github: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
        </svg>
      ),
      discord: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#5865F2">
          <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
        </svg>
      ),
      cfxre: (
        <img
          src={mode === 'dark' ? '/logo_fiveM_blanc.png' : '/logo_fiveM_noir.png'}
          alt="Cfx.re"
          width="24"
          height="24"
          style={{ objectFit: 'contain' }}
        />
      )
    }
    return icons[provider] || <Link20Regular />
  }

  // Get list of providers that are not yet linked
  const availableProviders = OAUTH_PROVIDERS.filter(
    p => p.id !== 'email' && !identities.some(i => i.provider === p.id)
  )

  if (loading) {
    return (
      <div className="page-content">
        <VStack gap="lg">
          <VStack gap="xs">
            <Skeleton width={200} height={32} />
            <Skeleton width={300} height={20} />
          </VStack>
          <VStack gap="md">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} width="100%" height={80} />
            ))}
          </VStack>
        </VStack>
      </div>
    )
  }

  return (
    <div className="page-content">
      <VStack gap="lg">
        {/* Header */}
      <VStack gap="xs">
        <Heading level={2}>Linked Accounts</Heading>
        <Text color="muted">
          Connect your accounts from other services to enable quick sign-in options.
        </Text>
      </VStack>

      {/* Warning for placeholder email users */}
      {user?.email?.includes(PLACEHOLDER_EMAIL_DOMAIN) && (
        <Banner variant="warning" title="Email Required">
          You need to add a real email address before you can manage your linked accounts.
          The email prompt should appear automatically.
        </Banner>
      )}

      {/* Linked Accounts */}
      <VStack gap="md">
        <Text weight="medium" size="sm" color="muted">Connected Accounts</Text>

        {identities.length === 0 ? (
          <Card padding="lg" variant="outlined">
            <VStack gap="sm" style={{ alignItems: 'center', padding: '1rem' }}>
              <Link20Regular style={{ fontSize: '2rem', color: 'var(--text-muted)' }} />
              <Text color="muted">No linked accounts yet</Text>
              <Text size="sm" color="muted">
                Connect accounts from other services to enable quick sign-in.
              </Text>
            </VStack>
          </Card>
        ) : (
          identities.map(identity => (
            <Card key={identity.id} padding="md">
              <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <HStack gap="md" style={{ alignItems: 'center' }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {getProviderIcon(identity.provider)}
                  </div>
                  <VStack gap="xs">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Text weight="medium">{getProviderLabel(identity.provider)}</Text>
                      <CheckmarkCircle20Filled style={{ color: 'var(--color-success)' }} />
                    </HStack>
                    <Text size="sm" color="muted">
                      {identity.provider_username || identity.provider_email || `ID: ${identity.provider_user_id}`}
                    </Text>
                    <Text size="xs" color="muted">
                      Linked {new Date(identity.linked_at).toLocaleDateString()}
                    </Text>
                  </VStack>
                </HStack>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnlinkClick(identity)}
                  loading={unlinking === identity.id}
                  disabled={identities.length <= 1 && user?.email?.includes(PLACEHOLDER_EMAIL_DOMAIN)}
                >
                  <Dismiss20Regular />
                  Unlink
                </Button>
              </HStack>
            </Card>
          ))
        )}
      </VStack>

      {/* Available Providers to Link */}
      {availableProviders.length > 0 && (
        <VStack gap="md">
          <Text weight="medium" size="sm" color="muted">Available to Connect</Text>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '0.75rem'
          }}>
            {availableProviders.map(provider => (
              <Card
                key={provider.id}
                padding="md"
                variant="outlined"
                hoverable
                onClick={() => handleLinkProvider(provider.id)}
                style={{ cursor: 'pointer' }}
              >
                <HStack gap="md" style={{ alignItems: 'center' }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {getProviderIcon(provider.id)}
                  </div>
                  <VStack gap="xs">
                    <Text weight="medium">{provider.label}</Text>
                    <Text size="xs" color="muted">Click to connect</Text>
                  </VStack>
                  <Add20Regular style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
                </HStack>
              </Card>
            ))}
          </div>
        </VStack>
      )}

      {/* Unlink Confirmation Modal */}
      <Modal
        open={showUnlinkModal}
        onClose={() => setShowUnlinkModal(false)}
        title="Unlink Account"
      >
        <VStack gap="md">
          <Text>
            Are you sure you want to unlink your <strong>{selectedIdentity ? getProviderLabel(selectedIdentity.provider) : ''}</strong> account?
          </Text>
          <Text size="sm" color="muted">
            You won't be able to sign in with this account anymore, but you can link it again later.
          </Text>

          <HStack gap="sm" style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => setShowUnlinkModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleUnlink}
              loading={unlinking !== null}
            >
              Unlink Account
            </Button>
          </HStack>
        </VStack>
      </Modal>
      </VStack>
    </div>
  )
}
